import { ClipboardList } from 'lucide-react';
import { useEffect, useState } from 'react';

import { api } from '../lib/api';
import { ATTRITION_CATEGORIES_CHANGED } from '../lib/navEvents';
import type { NavDynamicItems, NavNode } from './sidebarConfig';

interface CategoryNavItem {
  id: string;
  name: string;
  isActive: boolean;
}

/**
 * The sidebar's runtime leaves, by source — today only Attrition's categories.
 *
 * **Fetched only for a reader holding the leaves' own key**, so nobody else pays for the call or
 * learns the names. **A failed fetch leaves the source ABSENT**, never an empty list: the group
 * then shows only its static rows, and the landing walk still reaches the register through the
 * group's static landing route.
 *
 * Fetched through `lib/api` directly, not a feature's client — `layouts/` sits above the features.
 */
export function useNavDynamicItems(hasPermission: (permission: string) => boolean): NavDynamicItems {
  const canRead = hasPermission('attrition-requests.read');
  const [items, setItems] = useState<NavDynamicItems>({});
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    const bump = () => setReloadKey((k) => k + 1);
    window.addEventListener(ATTRITION_CATEGORIES_CHANGED, bump);

    return () => window.removeEventListener(ATTRITION_CATEGORIES_CHANGED, bump);
  }, []);

  useEffect(() => {
    if (!canRead) return;

    const controller = new AbortController();

    api<CategoryNavItem[]>('/api/attrition-requests/categories', { signal: controller.signal })
      .then((categories) => {
        if (controller.signal.aborted) return;

        const leaves: NavNode[] = categories.map((category) => ({
          key: `attrition-category-${category.id}`,
          label: category.name,
          icon: ClipboardList,
          to: `/attrition/categories/${category.id}`,
          permission: 'attrition-requests.read',
        }));

        setItems((current) => ({ ...current, 'attrition-categories': leaves }));
      })
      .catch(() => undefined);

    return () => controller.abort();
  }, [canRead, reloadKey]);

  // Losing the key drops the leaves this render, rather than leaving a stale list behind.
  return canRead ? items : {};
}
